import axios, { AxiosStatusCode } from '../axios';
import * as object from '../object';
import { UUID } from '../uuid';
import * as models from './models';
import { GetPageRequestID } from './pagerequests';

declare interface reCaptchaWindow extends Window {
    readonly reCaptchaV2SiteKey: string;
    readonly reCaptchaSiteKey: string;
}
declare const window: reCaptchaWindow;

export const ReCaptchaError = new Error('Please complete the reCAPTCHA and try again.');

export async function CreateFormSubmission(formSubmissionInit: models.IFormSubmissionCreateRequest): Promise<models.FormSubmissionCreateResponse> {
    const createRequest = new models.FormSubmissionCreateRequest(formSubmissionInit);
    if (!createRequest.pageRequestID) {
        // The page request carries the submission's context (IP, referer,
        // device, page URL) — the server stores only this link.
        createRequest.pageRequestID = await GetPageRequestID();
    }

    // The customer's time zone and the site language aren't derivable by the
    // processor — the user is created after this request is gone (in-request
    // after the submission commits, or an automation re-run), so neither the
    // browser nor the Accept-Language header the shared client sends is still
    // in hand. Both ride along in the payload for user creation.
    const payload = object.IsObject(createRequest.payload) ? createRequest.payload : {};
    if (payload['tz'] === undefined) {
        payload['tz'] = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    if (!payload['language']) {
        payload['language'] = document.documentElement.lang;
    }
    createRequest.payload = payload;

    return axios.post('/form-submissions', createRequest).then(r => {
        if (AxiosStatusCode(r) !== 201) {
            // reCAPTCHA verification failed: the server responds 200 with an
            // empty body instead of persisting (see smg.ReCAPTCHA). Surface it
            // as an empty response (no formSubmissionID) so gated forms can
            // fall back to their v2-checkbox retry.
            return new models.FormSubmissionCreateResponse();
        }
        return models.FormSubmissionCreateResponse.fromJSON(r.data);
    });
}

export interface CreateWithReCaptchaOpts {
    // Where the v2 checkbox renders when a v3 token is rejected.
    $reCaptchaPlaceholder: HTMLElement;
    // The reCAPTCHA v3 action label for this form, e.g. 'contact'.
    action: string;
}

/**
 * CreateFormSubmissionWithReCaptcha submits a form submission behind the
 * shared reCAPTCHA contract (the one the retired /contacts endpoint
 * established): a v2 checkbox response is required when the placeholder is
 * rendered (a previous v3 attempt was rejected); otherwise a v3 token is
 * minted, and if the server rejects it the v2 checkbox is rendered and this
 * rejects with ReCaptchaError so the user can retry.
 */
export async function CreateFormSubmissionWithReCaptcha(formSubmissionInit: models.IFormSubmissionCreateRequest, opts: CreateWithReCaptchaOpts): Promise<models.FormSubmissionCreateResponse> {
    const createRequest = new models.FormSubmissionCreateRequest(formSubmissionInit);

    // Resolves null when the server rejected the reCAPTCHA (its 200-no-body
    // contract) so each captcha path below can run its own retry behavior.
    const submit = async (reCaptcha: string, reCaptchaVersion: number) => {
        createRequest.reCaptcha = reCaptcha;
        createRequest.reCaptchaVersion = reCaptchaVersion;
        const submission = await CreateFormSubmission(createRequest);
        if (!submission.formSubmissionID) {
            return null;
        }
        return submission;
    };

    if (!opts.$reCaptchaPlaceholder.id) {
        opts.$reCaptchaPlaceholder.id = `recaptcha-${UUID()}`;
    }

    // A rendered placeholder means a v2 checkbox is already up (a previous v3
    // attempt was rejected) — its response is required.
    const v2 = Boolean(opts.$reCaptchaPlaceholder.innerHTML);
    if (v2) {
        const reCaptchaRenderID = parseInt(opts.$reCaptchaPlaceholder.getAttribute('data-widget-id') ?? '0', 10);
        const response = grecaptcha.getResponse(reCaptchaRenderID);
        if (!response) {
            throw ReCaptchaError;
        }

        try {
            const submission = await submit(response, 2);
            if (!submission) {
                throw ReCaptchaError;
            }
            return submission;
        } finally {
            grecaptcha.reset(reCaptchaRenderID);
        }
    }

    return new Promise((resolve, reject) => {
        grecaptcha.ready(async () => {
            try {
                const token = await grecaptcha.execute(window.reCaptchaSiteKey, { 'action': opts.action });

                const submission = await submit(token, 3);
                if (!submission) {
                    const reCaptchaRenderID = grecaptcha.render(opts.$reCaptchaPlaceholder.id, {
                        'sitekey': window.reCaptchaV2SiteKey,
                    });
                    opts.$reCaptchaPlaceholder.setAttribute('data-widget-id', String(reCaptchaRenderID));

                    reject(ReCaptchaError);
                    return;
                }

                resolve(submission);
            } catch (err) {
                reject(err);
            }
        });
    });
}

export async function FormSubmission(id: string): Promise<models.FormSubmissionGetResponse> {
    return axios.get(`/form-submissions/${id}`).then(r => models.FormSubmissionGetResponse.fromJSON(r.data));
}

export interface PollForResultOptions {
    // How long to keep polling before giving up. The routing automation is
    // near-real-time, so the default comfortably covers the normal case.
    timeoutMs?: number;
    // What "routing finished" means for this form. Defaults to any linked
    // result appearing (quoteRequestID, or a userID attach).
    isComplete?: (formSubmission: models.FormSubmissionGetResponse) => boolean;
}

/**
 * PollForResult polls GET /form-submissions/:id until the routing automation
 * (#6718) creates the resulting entity (user attach, contact, newsletter),
 * then resolves with the final response so the page can run its success
 * behavior (thank-you redirect, lead tracking, download unlock).
 * Quote-request submissions process synchronously — their result rides back
 * on the create response — so for them this is only the degraded fallback.
 *
 * Resolves null on timeout — a persisted submission is never an error, so the
 * caller should fall back to a generic "we got your request — you'll hear
 * from us" message rather than showing a failure.
 */
export async function PollForResult(id: string, options?: PollForResultOptions): Promise<models.FormSubmissionGetResponse | null> {
    const timeoutMs = options?.timeoutMs ?? 30_000;
    const isComplete = options?.isComplete ?? (formSubmission => Boolean(formSubmission.quoteRequestID) || Boolean(formSubmission.userID));

    const deadline = Date.now() + timeoutMs;
    let delay = 1000;
    for (;;) {
        try {
            const formSubmission = await FormSubmission(id);
            if (isComplete(formSubmission)) {
                return formSubmission;
            }
        } catch {
            // A flaky poll round is not a failed submission — keep polling
            // until the deadline; the shared axios instance already retries
            // transient network errors per request.
        }

        if (Date.now() + delay > deadline) {
            return null;
        }
        await new Promise(resolve => setTimeout(resolve, delay));
        delay = Math.min(delay * 1.5, 5000);
    }
}
